import apiClient from './client';

/** 产物（Artifact）编辑器：详情 / 文件 / 版本 / 依赖 / HITL 预览 API 客户端。 */

export interface ArtifactVersionInfo {
  id: string;
  version: string;
  changelog: string | null;
  state: string;
  created_at: string;
}

export interface ArtifactDetail {
  id: string;
  kind: string;
  name: string;
  framework: string;
  version: string;
  state: string;
  versions: ArtifactVersionInfo[];
}

export interface SkippedFile {
  path: string;
  reason: string;
}

export interface ArtifactFiles {
  files: Record<string, string>;
  skipped: SkippedFile[];
}

export interface LintResult {
  passed: boolean;
  errors: string[];
  warnings: string[];
}

export interface SaveFilesResult {
  lint: LintResult | null;
  version: string;
}

export interface DepEntry {
  artifact_id: string;
  name: string;
  kind: string;
  version: string;
  state: string;
  depth: number;
}

export interface ImpactDependent extends DepEntry {
  locked_member_version: string | null;
  stale: boolean;
}

export interface ImpactReport {
  artifact_id: string;
  version: string;
  dependent_count: number;
  published_dependent_count: number;
  stale_count: number;
  dependents: ImpactDependent[];
}

/** HITL checkpoint 契约条目（与 backend HitlCheckpoint schema 对齐） */
export interface HitlCheckpointEntry {
  id: string;
  name: string;
  phase: string;
  prompt: string;
  required_role?: string | null;
  blocking: boolean;
  diff?: Record<string, unknown>;
}

export type ArtifactState = 'draft' | 'published' | 'deprecated';

export async function getArtifact(artifactId: string): Promise<ArtifactDetail> {
  const res = await apiClient.get(`/artifacts/${artifactId}`);
  return res.data;
}

export async function getArtifactFiles(artifactId: string): Promise<ArtifactFiles> {
  const res = await apiClient.get(`/artifacts/${artifactId}/files`);
  return { files: res.data.files || {}, skipped: res.data.skipped || [] };
}

export async function saveArtifactFiles(
  artifactId: string,
  files: Record<string, string>,
  changelog?: string
): Promise<SaveFilesResult> {
  const res = await apiClient.put(`/artifacts/${artifactId}/files`, {
    files,
    changelog: changelog?.trim() || undefined,
  });
  return { lint: res.data.lint || null, version: res.data.version || '' };
}

/** 模型改写：apply=false 只产 diff 预览，apply=true 落盘并返回新版本号 */
export async function rewriteArtifact(
  artifactId: string,
  instruction: string,
  apply: boolean
): Promise<{ diff?: Record<string, string>; warnings?: string[]; version?: string }> {
  const res = await apiClient.post('/rewrite', { artifact_id: artifactId, instruction, apply });
  return res.data;
}

export async function transitionArtifact(artifactId: string, targetState: ArtifactState): Promise<ArtifactDetail> {
  const res = await apiClient.post(`/artifacts/${artifactId}/transition`, { target_state: targetState });
  return res.data;
}

/** 发布新版本（SemVer bump；major 破坏性变更后端强制 changelog） */
export async function bumpArtifactVersion(
  artifactId: string,
  part: 'major' | 'minor' | 'patch',
  changelog?: string
): Promise<{ version: string }> {
  const res = await apiClient.post(`/artifacts/${artifactId}/bump`, {
    part,
    changelog: changelog?.trim() || undefined,
  });
  return res.data;
}

/** 回滚到 published 历史版本（生成新版本快照，历史不改写） */
export async function rollbackArtifact(
  artifactId: string,
  targetVersion: string,
  changelog?: string
): Promise<{ version: string }> {
  const res = await apiClient.post(`/artifacts/${artifactId}/rollback`, {
    target_version: targetVersion,
    changelog: changelog?.trim() || undefined,
  });
  return res.data;
}

export async function getHitlCheckpoints(artifactId: string): Promise<HitlCheckpointEntry[]> {
  const res = await apiClient.get(`/artifacts/${artifactId}/hitl`);
  return res.data.checkpoints || [];
}

/** HITL 变更预览（P4）：契约未携带 diff 时，以后端 diff 服务生成示例变更的行级差异 */
export async function previewHitlDiff(
  artifactId: string,
  payload: {
    checkpoint_id: string;
    label: string;
    before: { prompt: string; blocking: boolean };
    after: { prompt: string; blocking: boolean };
  }
): Promise<Record<string, unknown>> {
  const res = await apiClient.post(`/artifacts/${artifactId}/hitl/preview`, payload);
  return res.data.diff;
}

export async function listArtifactDeps(artifactId: string, direction: 'down' | 'up'): Promise<DepEntry[]> {
  const res = await apiClient.get(`/artifacts/${artifactId}/deps`, { params: { direction } });
  return res.data.items || [];
}

export async function getArtifactImpact(artifactId: string): Promise<ImpactReport> {
  const res = await apiClient.get(`/artifacts/${artifactId}/impact`);
  return res.data;
}

/** 添加依赖（当前产物引用目标产物）；成环等 409 错误由调用方就地展示 */
export async function addArtifactDep(artifactId: string, toId: string): Promise<void> {
  await apiClient.post(`/artifacts/${artifactId}/deps`, { to_id: toId });
}

export async function removeArtifactDep(artifactId: string, toId: string): Promise<void> {
  await apiClient.delete(`/artifacts/${artifactId}/deps/${toId}`);
}
